import React from 'react';

// Unit conversion helpers (same as in App.js)
const convertAirflowFromM3H = (value, unit) => {
  if (value === null || value === undefined) return null;
  const numValue = Number(value);
  if (Number.isNaN(numValue)) return null;

  switch (unit) {
    case 'm3h':
      return numValue;
    case 'm3s':
      return numValue / 3600;
    case 'ls':
      return numValue / 3.6;
    case 'cfm':
      return numValue / 1.69901079552;
    default:
      return numValue;
  }
};

const convertPressureFromPa = (value, unit) => {
  if (value === null || value === undefined) return null;
  const numValue = Number(value);
  if (Number.isNaN(numValue)) return null;

  switch (unit) {
    case 'pa':
      return numValue;
    case 'mmh2o':
      return numValue / 9.80665;
    case 'inchh2o':
      return numValue / 249.08891;
    case 'mbar':
      return numValue / 100;
    case 'kpa':
      return numValue / 1000;
    default:
      return numValue;
  }
};

const getAirflowUnitLabel = (unit) => {
  switch (unit) {
    case 'm3h': return 'm³/h';
    case 'm3s': return 'm³/s';
    case 'ls': return 'l/s';
    case 'cfm': return 'cfm';
    default: return 'm³/h';
  }
};

const getPressureUnitLabel = (unit) => {
  switch (unit) {
    case 'pa': return 'Pa';
    case 'mmh2o': return 'mm H₂O';
    case 'inchh2o': return 'inch H₂O';
    case 'mbar': return 'mbar';
    case 'kpa': return 'kPa';
    default: return 'Pa';
  }
};

const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return num.toFixed(decimals);
};

export default function FansTable({ 
  fans = [], 
  onSelectFan, 
  selectedFan, 
  airFlowUnit = 'm3h',
  pressureUnit = 'pa'
}) {
  const airFlowLabel = getAirflowUnitLabel(airFlowUnit);
  const pressureLabel = getPressureUnitLabel(pressureUnit);

  return (
    <div className="table-responsive">
      <table className="table table-striped table-hover">
        <thead>
          <tr>
            <th>Fan Model</th>
            <th className="d-none d-md-table-cell">Air Flow ({airFlowLabel})</th>
            <th className="d-none d-lg-table-cell">Static Pressure ({pressureLabel})</th>
            <th className="d-none d-md-table-cell">Shaft Power (kW)</th>
            <th className="d-none d-xl-table-cell">Motor Power (kW)</th>
            <th className="d-none d-xl-table-cell">Fan Speed (rpm)</th>
            <th className="d-none d-lg-table-cell">Price (€)</th>
          </tr>
        </thead>
        <tbody>
          {fans.length ? fans.map((fan, i) => {
            const isSelected = !!(selectedFan && selectedFan.id === fan.id);
            const rowStyle = { cursor:'pointer' };
            const cellStyle = isSelected ? { background: '#eef2ff' } : undefined;
            
            // Use intersection point if available, otherwise fall back to performance_points or other fields
            const intersectionPoint = fan.intersectionPoint;
            
            // Convert intersection point values to client's chosen units
            const displayAirFlow = intersectionPoint 
              ? convertAirflowFromM3H(intersectionPoint.airFlow ?? intersectionPoint.airflow_m3h, airFlowUnit)
              : (fan.performance_points?.[0]?.airflow_m3h ?? fan.airFlow ?? fan.airflow_m3h);
            
            const displayPressure = intersectionPoint
              ? convertPressureFromPa(intersectionPoint.staticPressure ?? intersectionPoint.static_pressure_pa, pressureUnit)
              : (fan.performance_points?.[0]?.static_pressure_pa ?? fan.staticPressure ?? fan.static_pressure_pa);
            
            const displayShaftPower = intersectionPoint
              ? (intersectionPoint.shaftPower ?? intersectionPoint.shaft_power_kw)
              : (fan.performance_points?.[0]?.shaft_power_kw ?? fan.shaftPower ?? fan.shaft_power);
            
            return (
              <tr key={fan.id ?? i} onClick={() => onSelectFan(fan)} style={rowStyle} className={isSelected ? 'selected-row' : ''}>
                <td style={cellStyle}>
                  <div className="fw-bold">{fan.model_name ?? fan.model ?? fan.name ?? '-'}</div>
                  <div className="small text-muted d-md-none">
                    Flow: {formatNumber(displayAirFlow)} {airFlowLabel}
                    {displayPressure && ` • ${formatNumber(displayPressure)} ${pressureLabel}`}
                    {displayShaftPower && ` • ${formatNumber(displayShaftPower)} kW`}
                  </div>
                </td>
                <td className="d-none d-md-table-cell" style={cellStyle}>{formatNumber(displayAirFlow)}</td>
                <td className="d-none d-lg-table-cell" style={cellStyle}>{formatNumber(displayPressure)}</td>
                <td className="d-none d-md-table-cell" style={cellStyle}>{formatNumber(displayShaftPower)}</td>
                <td className="d-none d-xl-table-cell" style={cellStyle}>{fan.performance_points?.[0]?.motor_power_kw ?? fan.motorPower ?? fan.motor_power ?? '-'}</td>
                <td className="d-none d-xl-table-cell" style={cellStyle}>{fan.performance_points?.[0]?.speed_rpm ?? fan.fanSpeed ?? fan.rpm ?? '-'}</td>
                <td className="d-none d-lg-table-cell" style={cellStyle}>{fan.price ?? '-'}</td>
              </tr>
            );
          }) : (
            <tr><td colSpan={7} className="text-center">No fans available</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}